import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { api, ApiError, MIN_PASSWORD_LENGTH, PASSWORD_HINT } from '../api.js';
import { useSession } from '../App.js';

type Mode = 'signIn' | 'register';

export function SignIn() {
  const { me, loading, refresh } = useSession();
  const navigate = useNavigate();

  const [mode, setMode] = useState<Mode>('signIn');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  /** Asking for a reset link rather than signing in. */
  const [forgot, setForgot] = useState(false);
  const [sent, setSent] = useState(false);
  /** The address an account is waiting on a confirmation link at. Set by
   * a fresh signup, and by signing in to an account that never opened
   * one; while it is set, this screen is that message and nothing else. */
  const [awaiting, setAwaiting] = useState<string | null>(null);
  const [resent, setResent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (loading) return <div className="content"><p className="eyebrow">Loading</p></div>;
  if (me) return <Navigate to="/" replace />;

  const submit = async () => {
    setError(null);
    setBusy(true);
    try {
      if (mode === 'register') {
        const made = await api.register(username, password, email, firstName, lastName);
        // The usual case: the account exists but cannot sign in until the
        // link is opened, so there is nothing to sign in to yet. A site
        // with no mail sends no link and carries straight on.
        if (!made.verified) {
          setAwaiting(email.trim());
          return;
        }
      }
      await api.login(username, password);
      await refresh();
      navigate('/');
    } catch (err) {
      // 403 from the sign in route is the one thing it says out loud: the
      // password was right and the account is waiting on its link.
      if (err instanceof ApiError && err.status === 403 && err.body.unverified) {
        setAwaiting(typeof err.body.email === 'string' ? err.body.email : email.trim());
        return;
      }
      setError(err instanceof ApiError ? err.message : 'Could not reach the server.');
    } finally {
      setBusy(false);
    }
  };

  const resend = () => {
    setBusy(true);
    setError(null);
    void api.resendVerification(awaiting ?? email)
      .then(() => setResent(true))
      .catch((err) => setError(
        err instanceof ApiError ? err.message : 'Could not send that.',
      ))
      .finally(() => setBusy(false));
  };

  // Waiting on a link. Nothing on this screen signs anyone in: opening the
  // link is what makes that possible, and it is opened wherever the mail
  // was read rather than here.
  if (awaiting) {
    return (
      <div className="gate">
        <div className="gate-panel plate">
          <div className="hazard hazard-rule" role="presentation" />
          <div className="gate-body">
            <div>
              <p className="eyebrow">Check your email</p>
              <h1 className="gate-title">Rally<span className="mark-x">X</span></h1>
            </div>

            <p className="notice ok">
              A confirmation link is on its way to {awaiting}. Open it and the
              account is ready. The link works once and expires in 24 hours.
            </p>

            {error && <p className="notice">{error}</p>}

            {resent ? (
              <p className="mono muted">Sent again.</p>
            ) : (
              <button className="secondary" onClick={resend} disabled={busy}>
                Send it again
              </button>
            )}

            <button
              className="secondary"
              onClick={() => {
                setAwaiting(null);
                setResent(false);
                setError(null);
                setMode('signIn');
              }}
            >
              Back to sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  const canSubmit = username.length >= 3 && password.length >= MIN_PASSWORD_LENGTH && !busy
    // A new account needs an address: it is where the link that proves
    // the account goes.
    && (mode !== 'register' || email.includes('@'));

  return (
    <div className="gate">
      <div className="gate-panel plate">
        <div className="hazard hazard-rule" role="presentation" />
        <div className="gate-body">
          <div>
            <p className="eyebrow">Factory floor access</p>
            <h1 className="gate-title">Rally<span className="mark-x">X</span></h1>
          </div>

          <div className="stack" style={{ gap: '0.85rem' }}>
            <div>
              <label htmlFor="username">{mode === 'register' ? 'Username' : 'Username or email address'}</label>
              <input
                id="username"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && canSubmit) void submit(); }}
              />
            </div>
            <div>
              {mode === 'register' && (
                <>
                  <label htmlFor="email">Email address</label>
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <p className="mono muted" style={{ fontSize: '0.8125rem', marginTop: '0.4rem', marginBottom: '0.8rem' }}>
                    A link goes here to confirm the account, and it is the way
                    back in if you forget your password.
                  </p>
                  <label htmlFor="first-name">First name</label>
                  <input
                    id="first-name"
                    autoComplete="given-name"
                    maxLength={64}
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                  <label htmlFor="last-name" style={{ marginTop: '0.4rem' }}>Last name</label>
                  <input
                    id="last-name"
                    autoComplete="family-name"
                    maxLength={64}
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </>
              )}

              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && canSubmit) void submit(); }}
              />
              {mode === 'register' && (
                <p className="mono muted" style={{ fontSize: '0.8125rem', marginTop: '0.4rem' }}>
                  {PASSWORD_HINT}
                </p>
              )}
            </div>
          </div>

          {error && <p className="notice">{error}</p>}

          <button onClick={() => void submit()} disabled={!canSubmit}>
            {mode === 'register' ? 'Create account' : 'Sign in'}
          </button>

          <button
            className="secondary"
            onClick={() => { setMode(mode === 'signIn' ? 'register' : 'signIn'); setError(null); }}
          >
            {mode === 'signIn' ? 'Create an account' : 'I already have an account'}
          </button>

          {mode === 'signIn' && (
            <>
              {/* A reset link, for someone who cannot get in at all. */}
              {sent ? (
                <p className="notice ok">
                  If that address has an account, a reset link is on its way.
                  It works once and expires in two hours.
                </p>
              ) : forgot ? (
                <div className="stack" style={{ gap: '0.4rem' }}>
                  <label htmlFor="forgot-email">Your email address</label>
                  <input
                    id="forgot-email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <button
                    onClick={() => {
                      setBusy(true);
                      void api.forgotPassword(email)
                        // The answer is the same either way, deliberately:
                        // saying which addresses have accounts would let
                        // anyone enumerate the players.
                        .then(() => setSent(true))
                        .catch((err) => setError(
                          err instanceof ApiError ? err.message : 'Could not send that.',
                        ))
                        .finally(() => setBusy(false));
                    }}
                    disabled={!email.includes('@') || busy}
                  >
                    Send me a reset link
                  </button>
                </div>
              ) : (
                <button className="secondary" onClick={() => setForgot(true)}>
                  I have forgotten my password
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
